import { useState, type ReactNode } from "react";
import { ContextMenu as CM } from "radix-ui";
import { AlarmClock, AlarmClockOff, ArrowRightLeft, Ban, Check, ChevronRight, Copy, CopyPlus, Link2, RotateCcw, Star, StarOff, Tag, Trash2 } from "lucide-react";
import { PRIORITY_LABEL, STATUS_LABEL, TASK_STATUSES, type TaskWithFlags } from "@desk/contracts";
import { PriorityIcon, StatusIcon } from "@/components/icons";
import { useTaskActions } from "@/data/actions";
import { useHierarchy } from "@/data/hooks";
import { formatInstant, snoozePresets, toIsoUtc } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { CustomSnoozeDialog } from "@/features/snooze/SnoozeControls";
import { MoveDialog } from "@/features/destination/MoveDialog";
import { PRIORITY_KEYS, PRIORITY_ORDER } from "./TaskMenu";
import { taskRegistry } from "@/features/list/registry";
import { copyText } from "@/lib/clipboard";
import { duplicateAndOpen } from "./duplicate";

const content =
  "z-50 min-w-[210px] overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-pop outline-none data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out";
const item =
  "relative flex h-8 cursor-default select-none items-center gap-2 rounded-md px-2 text-[13.5px] outline-none data-[disabled]:opacity-45 data-[highlighted]:bg-hover [&_svg]:size-4 [&_svg]:shrink-0 [&_svg:not([data-tone])]:text-muted-foreground";

function Sub({ icon, label, hint, children }: { icon: ReactNode; label: string; hint?: string; children: ReactNode }) {
  return (
    <CM.Sub>
      <CM.SubTrigger className={cn(item, "data-[state=open]:bg-hover")}>
        {icon}
        {label}
        {hint && <span className="ml-auto pl-4 text-xs text-subtle">{hint}</span>}
        <ChevronRight className={cn(!hint && "ml-auto")} />
      </CM.SubTrigger>
      <CM.Portal>
        <CM.SubContent sideOffset={4} collisionPadding={8} className={content}>
          {children}
        </CM.SubContent>
      </CM.Portal>
    </CM.Sub>
  );
}

function Mark({ on }: { on: boolean }) {
  return <span className="ml-auto inline-flex w-4 justify-end">{on && <Check className="!text-foreground" />}</span>;
}

/** Right-click menu for any task row (Linear-style): Status, Priority, Labels, Today, Snooze, Move, Duplicate, Copy ID. */
export function TaskContextMenu({ task, children }: { task: TaskWithFlags; children: ReactNode }) {
  const a = useTaskActions();
  const h = useHierarchy();
  const [custom, setCustom] = useState(false);
  const [move, setMove] = useState(false);
  const closed = task.status === "done" || task.status === "cancelled";
  const planned = task.plannedDate === h.today;
  const knownLabels = () => {
    const s = new Set<string>(task.labels);
    taskRegistry.forEach((t) => t.labels.forEach((l) => s.add(l)));
    return [...s].sort((x, y) => x.localeCompare(y));
  };
  return (
    <>
      <CM.Root modal={false}>
        {/* display:contents wrapper so every concept row gets the right-click handler without forwarding props */}
        <CM.Trigger asChild>
          <div className="contents">{children}</div>
        </CM.Trigger>
        <CM.Portal>
          <CM.Content className={content} collisionPadding={8} onKeyDown={(e) => e.stopPropagation()}>
            <Sub icon={<StatusIcon status={task.status} />} label="Status" hint="S">
              {TASK_STATUSES.map((s) => (
                <CM.Item key={s} className={item} onSelect={() => void a.setStatus(task, s)}>
                  <StatusIcon status={s} />
                  {STATUS_LABEL[s]}
                  <Mark on={task.status === s} />
                </CM.Item>
              ))}
            </Sub>
            <Sub icon={<PriorityIcon priority={task.priority} />} label="Priority" hint="P">
              {PRIORITY_ORDER.map((p) => (
                <CM.Item key={p} className={item} onSelect={() => void a.setPriority(task, p)}>
                  <PriorityIcon priority={p} />
                  {PRIORITY_LABEL[p]}
                  <span className="ml-auto pl-3 text-xs text-subtle">{PRIORITY_KEYS[p]}</span>
                  <Mark on={task.priority === p} />
                </CM.Item>
              ))}
            </Sub>
            <Sub icon={<Tag />} label="Labels">
              {knownLabels().length === 0 && <div className="px-2 py-1.5 text-[13px] text-muted-foreground">No labels yet. Add one in the task.</div>}
              {knownLabels().map((l) => {
                const on = task.labels.includes(l);
                return (
                  <CM.Item
                    key={l}
                    className={item}
                    onSelect={(e) => {
                      e.preventDefault();
                      void a.update(task, { labels: on ? task.labels.filter((x) => x !== l) : [...task.labels, l] });
                    }}
                  >
                    {l}
                    <Mark on={on} />
                  </CM.Item>
                );
              })}
            </Sub>
            <CM.Separator className="-mx-1 my-1 h-px bg-border" />
            {!closed && (
              <CM.Item className={item} onSelect={() => void a.toggleToday(task)}>
                {planned ? <StarOff /> : <Star />}
                {planned ? "Remove from Today" : "Add to Today"}
                <span className="ml-auto pl-4 text-xs text-subtle">T</span>
              </CM.Item>
            )}
            {task.flags.isSnoozed ? (
              <CM.Item className={item} onSelect={() => void a.unsnooze(task)}>
                <AlarmClockOff />
                Unsnooze
              </CM.Item>
            ) : (
              !closed && (
                <Sub icon={<AlarmClock />} label="Snooze" hint="H">
                  {snoozePresets(h.timezone).map((p) => (
                    <CM.Item key={p.key} className={item} onSelect={() => void a.snooze(task, toIsoUtc(p.at))}>
                      {p.label}
                      <span className="ml-auto pl-6 text-[12px] text-subtle">{formatInstant(p.at.toISO()!, h.timezone)}</span>
                    </CM.Item>
                  ))}
                  <CM.Item className={item} onSelect={() => setCustom(true)}>
                    Custom…
                  </CM.Item>
                </Sub>
              )
            )}
            <CM.Item className={item} onSelect={() => setMove(true)}>
              <ArrowRightLeft />
              Move to…
              <span className="ml-auto pl-4 text-xs text-subtle">M</span>
            </CM.Item>
            <CM.Item className={item} onSelect={() => void duplicateAndOpen(a, task)}>
              <CopyPlus />
              Duplicate
              <span className="ml-auto pl-4 text-xs text-subtle">⌘D</span>
            </CM.Item>
            <CM.Separator className="-mx-1 my-1 h-px bg-border" />
            <CM.Item
              className={item}
              onSelect={() => {
                void copyText(task.readableId, `Copied ${task.readableId}`);
              }}
            >
              <Copy />
              Copy ID
            </CM.Item>
            <CM.Item
              className={item}
              onSelect={() => {
                void copyText(`${location.origin}${location.pathname}#/task/${task.readableId}`, "Copied link");
              }}
            >
              <Link2 />
              Copy link
            </CM.Item>
            <CM.Separator className="-mx-1 my-1 h-px bg-border" />
            {closed ? (
              <CM.Item className={item} onSelect={() => void a.reopen(task)}>
                <RotateCcw />
                Reopen
              </CM.Item>
            ) : (
              <CM.Item className={item} onSelect={() => void a.cancel(task)}>
                <Ban />
                Cancel task
              </CM.Item>
            )}
            <CM.Item className={cn(item, "text-destructive [&_svg]:text-destructive")} onSelect={() => void a.remove(task)}>
              <Trash2 />
              Delete
              <span className="ml-auto pl-4 text-xs text-subtle">⌘⌫</span>
            </CM.Item>
          </CM.Content>
        </CM.Portal>
      </CM.Root>
      <CustomSnoozeDialog open={custom} onOpenChange={setCustom} onConfirm={(iso) => void a.snooze(task, iso)} />
      <MoveDialog task={task} open={move} onOpenChange={setMove} />
    </>
  );
}
